import { liquidMetalFragmentShader, ShaderMount } from '@paper-design/shaders';
import { Send } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface LiquidMetalButtonProps {
  label?: string;
  onClick?: () => void;
  viewMode?: 'text' | 'icon';
  icon?: React.ReactNode;
}

export function LiquidMetalButton({
  label = 'Get Started',
  onClick,
  viewMode = 'text',
  icon,
}: LiquidMetalButtonProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [ripples, setRipples] = useState<Array<{ x: number; y: number; id: number }>>([]);
  const shaderRef = useRef<HTMLDivElement>(null);
  // biome-ignore lint/suspicious/noExplicitAny: External library without types
  const shaderMount = useRef<any>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const rippleId = useRef(0);

  const dimensions = useMemo(() => {
    if (viewMode === 'icon') {
      return {
        width: 46,
        height: 46,
        innerWidth: 42,
        innerHeight: 42,
        shaderWidth: 46,
        shaderHeight: 46,
      };
    } else {
      return {
        width: 200,
        height: 58,
        innerWidth: 194,
        innerHeight: 52,
        shaderWidth: 200,
        shaderHeight: 58,
      };
    }
  }, [viewMode]);

  useEffect(() => {
    const styleId = 'shader-canvas-style-exploded';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .shader-container-exploded canvas {
          width: 100% !important;
          height: 100% !important;
          display: block !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          border-radius: 100px !important;
        }
        @keyframes ripple-animation {
          0% {
            transform: translate(-50%, -50%) scale(0);
            opacity: 0.6;
          }
          100% {
            transform: translate(-50%, -50%) scale(4);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);
    }

    const loadShader = async () => {
      try {
        if (shaderRef.current) {
          if (shaderMount.current?.destroy) {
            shaderMount.current.destroy();
          }

          shaderMount.current = new ShaderMount(
            shaderRef.current,
            liquidMetalFragmentShader,
            {
              u_repetition: 6,
              u_softness: 0.5,
              u_shiftRed: 0.3,
              u_shiftBlue: 0.3,
              u_distortion: 0,
              u_contour: 0,
              u_angle: 45,
              u_scale: 12,
              u_shape: 1,
              u_offsetX: 0.1,
              u_offsetY: -0.1,
            },
            undefined,
            0.6
          );
        }
      } catch (error) {
        console.error('[v0] Failed to load shader:', error);
      }
    };

    loadShader();

    return () => {
      if (shaderMount.current?.destroy) {
        shaderMount.current.destroy();
        shaderMount.current = null;
      }
    };
  }, []);

  const handleMouseEnter = () => {
    setIsHovered(true);
    shaderMount.current?.setSpeed?.(1);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setIsPressed(false);
    shaderMount.current?.setSpeed?.(0.6);
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (shaderMount.current?.setSpeed) {
      shaderMount.current.setSpeed(2.4);
      setTimeout(() => {
        if (isHovered) {
          shaderMount.current?.setSpeed?.(1);
        } else {
          shaderMount.current?.setSpeed?.(0.6);
        }
      }, 300);
    }

    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const ripple = { x, y, id: rippleId.current++ };

      setRipples((prev) => [...prev, ripple]);
      setTimeout(() => {
        setRipples((prev) => prev.filter((r) => r.id !== ripple.id));
      }, 600);
    }

    onClick?.();
  };

  return (
    <div data-component-id="src/components/ui/liquid-metal-button.tsx:160:4" data-component-path="src/components/ui/liquid-metal-button.tsx" data-component-line="160" data-component-end-line="350" data-component-file="liquid-metal-button.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20inline-block%22%7D" className="relative inline-block">
      <div data-component-id="src/components/ui/liquid-metal-button.tsx:161:6" data-component-path="src/components/ui/liquid-metal-button.tsx" data-component-line="161" data-component-end-line="349" data-component-file="liquid-metal-button.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        style={{
          perspective: '1000px',
          perspectiveOrigin: '50% 50%',
        }}
      >
        <div data-component-id="src/components/ui/liquid-metal-button.tsx:167:8" data-component-path="src/components/ui/liquid-metal-button.tsx" data-component-line="167" data-component-end-line="348" data-component-file="liquid-metal-button.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
          style={{
            position: 'relative',
            width: `${dimensions.width}px`,
            height: `${dimensions.height}px`,
            transformStyle: 'preserve-3d',
            transition:
              'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), width 0.4s ease, height 0.4s ease',
            transform: 'none',
          }}
        >
          <div data-component-id="src/components/ui/liquid-metal-button.tsx:178:10" data-component-path="src/components/ui/liquid-metal-button.tsx" data-component-line="178" data-component-end-line="226" data-component-file="liquid-metal-button.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: `${dimensions.width}px`,
              height: `${dimensions.height}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transformStyle: 'preserve-3d',
              transition:
                'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), width 0.4s ease, height 0.4s ease, gap 0.4s ease',
              transform: 'translateZ(20px)',
              zIndex: 30,
              pointerEvents: 'none',
            }}
          >
            {viewMode === 'icon' &&
              (icon || (
                <Send data-component-id="src/components/ui/liquid-metal-button.tsx:199:16" data-component-path="src/components/ui/liquid-metal-button.tsx" data-component-line="199" data-component-end-line="207" data-component-file="liquid-metal-button.tsx" data-component-name="Send" data-component-content="%7B%22elementName%22%3A%22Send%22%7D"
                  size={14}
                  style={{
                    color: '#e5e5e5',
                    filter: 'drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.5))',
                    transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    transform: 'scale(1)',
                  }}
                />
              ))}
            {viewMode === 'text' && (
              <span data-component-id="src/components/ui/liquid-metal-button.tsx:210:14" data-component-path="src/components/ui/liquid-metal-button.tsx" data-component-line="210" data-component-end-line="224" data-component-file="liquid-metal-button.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                style={{
                  fontSize: '12px',
                  color: '#E5E5E5',
                  fontWeight: 500,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  textShadow: '0px 1px 2px rgba(0, 0, 0, 0.5)',
                  transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  transform: 'scale(1)',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </span>
            )}
          </div>

          <div data-component-id="src/components/ui/liquid-metal-button.tsx:228:10" data-component-path="src/components/ui/liquid-metal-button.tsx" data-component-line="228" data-component-end-line="256" data-component-file="liquid-metal-button.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: `${dimensions.width}px`,
              height: `${dimensions.height}px`,
              transformStyle: 'preserve-3d',
              transition:
                'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), width 0.4s ease, height 0.4s ease',
              transform: `translateZ(10px) ${isPressed ? 'translateY(1px) scale(0.98)' : 'translateY(0) scale(1)'}`,
              zIndex: 20,
            }}
          >
            <div data-component-id="src/components/ui/liquid-metal-button.tsx:242:12" data-component-path="src/components/ui/liquid-metal-button.tsx" data-component-line="242" data-component-end-line="255" data-component-file="liquid-metal-button.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              style={{
                width: `${dimensions.innerWidth}px`,
                height: `${dimensions.innerHeight}px`,
                margin: '2px',
                borderRadius: '100px',
                background: 'linear-gradient(180deg, rgba(22,22,22,1) 0%, rgba(5,5,5,1) 100%)',
                boxShadow: isPressed
                  ? 'inset 0px 2px 4px rgba(0, 0, 0, 0.4), inset 0px 1px 2px rgba(0, 0, 0, 0.3)'
                  : 'none',
                transition:
                  'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), width 0.4s ease, height 0.4s ease, box-shadow 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          </div>

          <div data-component-id="src/components/ui/liquid-metal-button.tsx:258:10" data-component-path="src/components/ui/liquid-metal-button.tsx" data-component-line="258" data-component-end-line="301" data-component-file="liquid-metal-button.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: `${dimensions.width}px`,
              height: `${dimensions.height}px`,
              transformStyle: 'preserve-3d',
              transition:
                'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), width 0.4s ease, height 0.4s ease',
              transform: `translateZ(0px) ${isPressed ? 'translateY(1px) scale(0.98)' : 'translateY(0) scale(1)'}`,
              zIndex: 10,
            }}
          >
            <div data-component-id="src/components/ui/liquid-metal-button.tsx:272:12" data-component-path="src/components/ui/liquid-metal-button.tsx" data-component-line="272" data-component-end-line="300" data-component-file="liquid-metal-button.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              style={{
                height: `${dimensions.height}px`,
                width: `${dimensions.width}px`,
                borderRadius: '100px',
                boxShadow: isPressed
                  ? '0px 0px 0px 1px rgba(0, 0, 0, 0.5), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)'
                  : isHovered
                    ? '0px 0px 0px 1px rgba(0, 0, 0, 0.4), 0px 12px 6px 0px rgba(0, 0, 0, 0.05), 0px 8px 5px 0px rgba(0, 0, 0, 0.1), 0px 4px 4px 0px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.2)'
                    : '0px 0px 0px 1px rgba(0, 0, 0, 0.3), 0px 36px 14px 0px rgba(0, 0, 0, 0.02), 0px 20px 12px 0px rgba(0, 0, 0, 0.08), 0px 9px 9px 0px rgba(0, 0, 0, 0.12), 0px 2px 5px 0px rgba(0, 0, 0, 0.15)',
                transition:
                  'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), width 0.4s ease, height 0.4s ease, box-shadow 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                background: 'rgb(0 0 0 / 0)',
              }}
            >
              <div data-component-id="src/components/ui/liquid-metal-button.tsx:287:14" data-component-path="src/components/ui/liquid-metal-button.tsx" data-component-line="287" data-component-end-line="299" data-component-file="liquid-metal-button.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22shader-container-exploded%22%7D"
                ref={shaderRef}
                className="shader-container-exploded"
                style={{
                  borderRadius: '100px',
                  overflow: 'hidden',
                  position: 'relative',
                  width: `${dimensions.shaderWidth}px`,
                  maxWidth: `${dimensions.shaderWidth}px`,
                  height: `${dimensions.shaderHeight}px`,
                  transition: 'width 0.4s ease, height 0.4s ease',
                }}
              />
            </div>
          </div>

          <button data-component-id="src/components/ui/liquid-metal-button.tsx:303:10" data-component-path="src/components/ui/liquid-metal-button.tsx" data-component-line="303" data-component-end-line="347" data-component-file="liquid-metal-button.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
            ref={buttonRef}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onMouseDown={() => setIsPressed(true)}
            onMouseUp={() => setIsPressed(false)}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: `${dimensions.width}px`,
              height: `${dimensions.height}px`,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              outline: 'none',
              zIndex: 40,
              transformStyle: 'preserve-3d',
              transform: 'translateZ(25px)',
              transition:
                'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), width 0.4s ease, height 0.4s ease',
              overflow: 'hidden',
              borderRadius: '100px',
            }}
            aria-label={label}
          >
            {ripples.map((ripple) => (
              <span data-component-id="src/components/ui/liquid-metal-button.tsx:331:14" data-component-path="src/components/ui/liquid-metal-button.tsx" data-component-line="331" data-component-end-line="345" data-component-file="liquid-metal-button.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                key={ripple.id}
                style={{
                  position: 'absolute',
                  left: `${ripple.x}px`,
                  top: `${ripple.y}px`,
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background:
                    'radial-gradient(circle, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0) 70%)',
                  pointerEvents: 'none',
                  animation: 'ripple-animation 0.6s ease-out',
                }}
              />
            ))}
          </button>
        </div>
      </div>
    </div>
  );
}
